import {
  arrow,
  autoUpdate,
  computePosition,
  flip,
  offset,
  type Placement,
  type Side,
  shift,
  size,
} from "@floating-ui/dom";

export const createTooltip = () => {
  const references = Array.from(
    document.querySelectorAll<HTMLElement>("[data-tooltip]"),
  );

  references.forEach(tooltip);
};

const tooltip = (reference: HTMLElement) => {
  const trigger = reference.dataset.tooltipTrigger ?? "hover";

  const state: {
    floating?: HTMLElement;
    autoUpdate?: () => void;
    controller?: AbortController;
    offset: number;
  } = {
    offset: 8,
  };

  const closeTooltip = () => {
    state.floating?.remove();
    state.floating = undefined;
    state.autoUpdate?.();
    state.controller?.abort();
  };

  const openTooltip = (event: MouseEvent) => {
    event.stopPropagation();

    if (state.floating !== undefined) {
      closeTooltip();

      return;
    }

    const floating = document.createElement("div");

    floating.classList.add("popup");

    const tooltip = document.createElement("div");

    tooltip.classList.add(
      ...["tooltip", reference.dataset.tooltipTheme].filter(
        (token) => typeof token === "string",
      ),
    );
    tooltip.innerHTML = reference.dataset.tooltipContent ?? "";

    const arrowElement = createArrow();

    floating.appendChild(tooltip);
    floating.appendChild(arrowElement);
    document.body.appendChild(floating);

    state.floating = floating;
    state.autoUpdate = autoUpdate(reference, floating, () =>
      update(reference, floating, arrowElement),
    );

    state.controller = new AbortController();

    document.addEventListener(
      "click",
      (event) => {
        if (state.floating?.contains(event.target as Node)) {
          return;
        }

        closeTooltip();
      },
      {
        signal: state.controller.signal,
      },
    );

    const dismiss = Number.parseInt(
      reference.dataset.tooltipDismissAfter ?? "",
      10,
    );

    if (Number.isNaN(dismiss) !== undefined) {
      setTimeout(closeTooltip, dismiss);
    }
  };

  const update = async (
    reference: HTMLElement,
    floating: HTMLElement,
    arrowElement: SVGSVGElement,
  ) => {
    const { x, y, middlewareData, placement, strategy } = await computePosition(
      reference,
      floating,
      {
        strategy: "fixed",
        placement:
          (reference.dataset.placement as Placement | undefined) ?? "top",
        middleware: [
          offset(state.offset),
          flip(),
          shift({
            mainAxis: true,
            crossAxis: true,
            padding: state.offset,
          }),
          size({
            apply: ({ availableHeight, elements }) => {
              elements.floating.style.setProperty(
                "max-height",
                `${availableHeight - state.offset}px`,
              );
            },
          }),
          arrow({ element: arrowElement }),
        ],
      },
    );

    Object.assign(floating.style, {
      position: strategy,
      left: `${x}px`,
      top: `${y}px`,
      minWidth: `${reference.clientWidth}px`,
    });

    if (middlewareData.arrow !== undefined) {
      const side = placement.split("-")[0] as Side;

      const staticSide = {
        top: "bottom",
        right: "left",
        bottom: "top",
        left: "right",
      }[side];

      const rotate = {
        top: "180deg",
        right: "-90deg",
        bottom: "0",
        left: "90deg",
      }[side];

      Object.assign(arrowElement.style, {
        position: "absolute",
        left:
          middlewareData.arrow.x !== undefined
            ? `${middlewareData.arrow.x}px`
            : "auto",
        top:
          middlewareData.arrow.y !== undefined
            ? `${middlewareData.arrow.y}px`
            : "auto",
        [staticSide]: `${arrowElement.clientHeight * -1}px`,
        transform: `rotate(${rotate})`,
      });
    }
  };

  switch (trigger) {
    case "click": {
      reference.addEventListener("click", openTooltip);
      break;
    }
    case "hover": {
      reference.addEventListener("pointerenter", openTooltip);
      break;
    }
  }
};

const createArrow = () => {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");

  svg.classList.add("arrow");
  svg.setAttribute("preserveAspectRatio", "xMidYMax");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("width", "40");
  svg.setAttribute("height", "40");
  svg.setAttribute("viewBox", "0 0 40 40");

  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");

  path.setAttribute("stroke", "none");
  path.setAttribute(
    "d",
    "M40 40H0H6.56072C9.54592 40 12.4239 38.8786 14.6328 36.8549L17.982 33.7863C19.1262 32.7379 20.8738 32.7379 22.018 33.7863L25.3672 36.8549C27.5761 38.8786 30.4541 40 33.4393 40H40Z",
  );

  svg.appendChild(path);

  return svg;
};
